import { LIMITS } from './types';
import type { KeyValueStore, TrackEvent } from './types';

export interface QueuedEvent {
  seq: number;
  at: number;
  event: TrackEvent;
}

const isQueued = (value: unknown): value is QueuedEvent => {
  if (typeof value !== 'object' || value === null) return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.seq === 'number' &&
    typeof item.at === 'number' &&
    typeof item.event === 'object' &&
    item.event !== null
  );
};

/**
 * Events waiting to be sent, kept in order and mirrored to sessionStorage so a reload
 * or an unsent pagehide batch is not lost. Items leave the queue only once the server
 * has acknowledged them: the endpoint is idempotent, so sending one twice is safe.
 */
export class EventQueue {
  private items: QueuedEvent[] = [];
  private nextSeq = 1;
  private readonly store: KeyValueStore | null;
  private readonly key: string;

  constructor(store: KeyValueStore | null, key: string) {
    this.store = store;
    this.key = key;
    this.restore();
  }

  get size(): number {
    return this.items.length;
  }

  push(event: TrackEvent, now: number): void {
    this.items.push({ seq: this.nextSeq++, at: now, event });
    if (this.items.length > LIMITS.maxQueue) {
      this.items.splice(0, this.items.length - LIMITS.maxQueue);
    }
    this.persist();
  }

  /** Drops events the server would reject as too old anyway. */
  purgeStale(now: number): void {
    const kept = this.items.filter((item) => now - item.at <= LIMITS.maxEventAgeMs);
    if (kept.length !== this.items.length) {
      this.items = kept;
      this.persist();
    }
  }

  /** The next batch (a copy; nothing is removed): at most 20 events and about 8 kB. */
  takeBatch(): QueuedEvent[] {
    const batch: QueuedEvent[] = [];
    let bytes = 2;
    for (const item of this.items) {
      const size = JSON.stringify(item.event).length + 1;
      if (batch.length >= LIMITS.maxBatch) break;
      if (batch.length > 0 && bytes + size > LIMITS.maxBatchBytes) break;
      batch.push(item);
      bytes += size;
    }
    return batch;
  }

  remove(sent: QueuedEvent[]): void {
    const gone = new Set(sent.map((item) => item.seq));
    this.items = this.items.filter((item) => !gone.has(item.seq));
    this.persist();
  }

  clear(): void {
    this.items = [];
    this.persist();
  }

  private restore(): void {
    try {
      const raw = this.store?.getItem(this.key);
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed) || !parsed.every(isQueued)) return;
      this.items = parsed;
      this.nextSeq = parsed.reduce((max, item) => Math.max(max, item.seq), 0) + 1;
    } catch {
      // Unreadable storage: start empty, tracking must never break the app.
    }
  }

  private persist(): void {
    try {
      if (this.items.length === 0) this.store?.removeItem(this.key);
      else this.store?.setItem(this.key, JSON.stringify(this.items));
    } catch {
      // Storage full or blocked: the in-memory queue still works.
    }
  }
}
